import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { CustomCard } from '../customCard';
import { useFixFlipStore, useLoanStore, useManualQuoteStore } from '@roc/feature-loans';
import { isFixAndFlip, isGroundUp, isStabilizedBridge, LoanSubType } from '@roc/feature-utils';
import { useCallback, useEffect } from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { createTextField } from '@roc/ui';
import { EconomicsStep, FixFlipGULeverageMatrixCard, FixFlipLeverageCard, FixFlipLoanDetailsCard, FixFlipSuggestedLeverageCard, GroundUpLeverageCard, GroundUpLoanDetailsCard, GroundUpSuggestedLeverageCard, LeverageStep, StabilizedBridgeLeverageCard, StabilizedBridgeLoanDetailsCard, StabilizedBridgeSuggestedLeverageCard, useOneToolStore } from '@roc/feature-one-tool'

export const ManualQuoteReviseTerms = observer(() => {
  const { loanStore } = useLoanStore();
  const { loanDetails } = loanStore;
  const { oneToolStore } = useOneToolStore();
  const { globalStore } = useStore();
  const { manualQuoteStore } = useManualQuoteStore();
  const { fixFlipQuoteStore, groundUpQuoteStore, stabilizedBridgeQuoteStore } = oneToolStore;

  const getCorrectStoreBaseOnLoanType = () => {
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      return fixFlipQuoteStore;
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      return groundUpQuoteStore;
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      return stabilizedBridgeQuoteStore;
    }
  }

  const store = getCorrectStoreBaseOnLoanType();
  const loanData = store.getQuoteData();
  const { loanEconomicsStore } = store;

  const onPercentageChange = useCallback(() => {
    loanEconomicsStore.fetchQuoteOutputsDebounced();
    if (isGroundUp(loanData?.loanSubtype)) {
      const ltc = loanEconomicsStore.enableManualQuote ?
        loanEconomicsStore.manualExceptionQuoteFormStore?.calculatedFields?.calculatedLTFC :
        loanEconomicsStore.instaLeverageQuoteFormStore?.calculatedFields?.calculatedLTFC;
      const calculatedFields = loanEconomicsStore.caclulateFields(
        store.propertiesStore.getProperties(),
        loanEconomicsStore.instaLeverageQuoteFormStore.getFormValues()
      );
      store.loanEconomicsStore.getRateFloor({
        lenderName: '',
        submissionDate: new Date().toJSON(),
        leverage: ltc,
        purchasePrice: loanData?.properties[0]?.purchasePrice || 0,
        permitsInPlace: hasPermitsInPlace(loanData?.properties),
        constructionCost: loanData?.properties[0]?.renovationBudget || 0,
        interestExpense: calculatedFields?.interestExpense,
        arv: loanData?.properties[0]?.marketValueAfterCompletion || 0,
        propertyState: loanData?.properties[0]?.state,
        enableFinancedIr: isGroundUp(loanData?.loanSubtype),
        loanSubtype: loanData?.loanSubtype,
        softCostsOnly: calculateSoftsCostsOnly(),
        lenderId: globalStore.lenderInfo?.lenderId
      });
      store.loadInternalValuationFields()
    } else if (isFixAndFlip(loanData?.loanSubtype)) {
      const isPropertyAndBorrowerHighBoxCriteriaMet = loanEconomicsStore.showHighLeverageBoxPricingAdjustments;
      const ltc = loanEconomicsStore.enableManualQuote ?
        loanEconomicsStore.manualExceptionQuoteFormStore?.form.fields.loanToCost.value :
        loanEconomicsStore.instaLeverageQuoteFormStore?.form.fields.loanToCost.value;
      const calculatedFields = loanEconomicsStore.caclulateFields(
        store.propertiesStore.getProperties(),
        loanEconomicsStore.instaLeverageQuoteFormStore.getFormValues()
      );
      store.loanEconomicsStore.getRateFloor({
        lenderName: '',
        submissionDate: new Date().toJSON(),
        leverage: ltc,
        purchasePrice: loanData?.properties[0]?.purchasePrice || 0,
        permitsInPlace: hasPermitsInPlace(loanData?.properties),
        constructionCost: loanData?.properties[0]?.renovationBudget || 0,
        interestExpense: calculatedFields?.interestExpense,
        arv: loanData?.properties[0]?.marketValueAfterCompletion || 0,
        propertyState: loanData?.properties[0]?.state,
        enableFinancedIr: isGroundUp(loanData?.loanSubtype),
        loanSubtype: loanData?.loanSubtype,
        isHighLeverageBox: loanEconomicsStore.meetsAllHighFicoCriteria,
        lenderId: globalStore.lenderInfo?.lenderId
      });
      store.loadInternalValuationFields()
    } else {
      store.loanEconomicsStore.fetchFloorRate();
    }
    store.loanEconomicsStore.fetchBorrowerPointsOptions();
  }, []);

  const hasPermitsInPlace = properties => {
    return properties.length === 1 && properties[0]?.permitsInPlace;
  };

  const calculateSoftsCostsOnly = () => {
    for (const property of loanData?.properties || []) {
      if (property?.softCostsOnly === false) {
        return false;
      }
    }
    return true;
  }

  const getCorrectLeverageCardByLoanType = () => {
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      return FixFlipLeverageCard;
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      return GroundUpLeverageCard;
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      return StabilizedBridgeLeverageCard;
    }
  }

  const getCorrectSuggestedLeverageCardByLoanType = () => {
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      return FixFlipSuggestedLeverageCard;
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      return GroundUpSuggestedLeverageCard;
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      return StabilizedBridgeSuggestedLeverageCard;
    }
    return undefined;
  }

  const getCorrectLoanDetailsCardByLoanType = () => {
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      return FixFlipLoanDetailsCard;
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      return GroundUpLoanDetailsCard;
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      return StabilizedBridgeLoanDetailsCard;
    }
  }

  return (
    <>
      <Grid item xs={12} style={{ marginBottom: 20 }}>
        <CustomCard>
          <LeverageStep
            store={store}
            allowInstaQuote={false}
            LeverageMatrixCard={FixFlipGULeverageMatrixCard}
            LeverageCard={getCorrectLeverageCardByLoanType()}
            SuggestedLeverageCard={getCorrectSuggestedLeverageCardByLoanType()}
            isManualQuote={true}
            onPercentageChange={onPercentageChange}
          />
        </CustomCard>
      </Grid>

      <Grid item xs={12} style={{ marginBottom: 20 }}>
        <CustomCard>
          <EconomicsStep
            store={store}
            LoanDetailsCard={getCorrectLoanDetailsCardByLoanType()}
            isManualQuote={true}
          />
        </CustomCard>
      </Grid>

      {loanStore?.lastFetchedCustomQuote?.source === 'cportal' &&
        <Grid item xs={12} style={{ marginBottom: 20 }}>
          <CustomCard title={'Underwriter Comment'}>
            {createTextField({
              store: manualQuoteStore.declineQuoteFormStore,
              testId: 'message',
              fieldName: 'message',
              label: 'Please provide any additional details or context here',
              multiline: true,
              minRows: 10,
              disabled: false,
            })}
          </CustomCard>
        </Grid>
      }
    </>
  );
});
